import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import { CbhIcon, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { isBefore } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { useClockInTimeValidation } from "../../Timekeeping/hooks/useClockInTimeValidation";
import {
  getBottomSheetIntent,
  TimekeepingAdjustmentBottomSheet,
} from "../../Timekeeping/TimekeepingAdjustmentBottomSheet";
import { getDayIndicator } from "../../Timekeeping/Timesheet/utils/getDayIndicator";
import { inferDateFromTime } from "../../Timekeeping/Timesheet/utils/inferDateFromTime";
import {
  getLocalTimeForPicker,
  getUtcTimeFromPicker,
} from "../../Timekeeping/Timesheet/utils/timezoneConversion";

export interface AdjustClockInBottomSheetProps {
  modalState: UseModalState;
  originalTime: Date;
  workplaceTimezone: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  existingReason?: string;
  /**
   * Time to pre-populate in the picker. Defaults to `originalTime` when not provided.
   * Use this when the worker has an existing adjustment and you want the picker to open
   * at their previously adjusted time rather than the true original.
   */
  initialTime?: Date;
  onConfirm: (newTime: Date, reason: string) => void;
  onCancel?: () => void;
  shiftId?: string;
  geofenceArrivalTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  workplaceSettings?: { preventEarlyClockIns: boolean; earlyClockInMinutes?: number };
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockIn?: Date;
  workerClockIn?: Date;
  clockOutTime?: Date;
  breakStartTime?: Date;
  isGeofenceWorkplace?: boolean;
  isNfcOrMeshWorkplace?: boolean;
}

export function AdjustClockInBottomSheet(props: AdjustClockInBottomSheetProps) {
  const {
    modalState,
    originalTime,
    workplaceTimezone,
    shiftStartAt,
    shiftEndAt,
    existingReason,
    initialTime,
    onConfirm,
    onCancel,
    shiftId,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    workplaceSettings,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockIn,
    workerClockIn,
    clockOutTime,
    breakStartTime,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
  } = props;
  const [isPickerScrolling, setIsPickerScrolling] = useState(false);

  const pickerSeedTime = initialTime ?? originalTime;

  const { setValue, watch, reset, formState } = useForm<{ time?: Date }>({
    defaultValues: {
      time: getLocalTimeForPicker(pickerSeedTime, workplaceTimezone),
    },
  });

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      reset({ time: getLocalTimeForPicker(pickerSeedTime, workplaceTimezone) });
    }
  }, [modalState.modalIsOpen, pickerSeedTime, reset, workplaceTimezone]);

  const currentLocalTime = watch("time");
  const currentTime = isDefined(currentLocalTime)
    ? getUtcTimeFromPicker(currentLocalTime, workplaceTimezone)
    : undefined;

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
        earlyBufferMinutes: 30,
      });
      setValue("time", inferResult.success ? inferResult.date : newTime, { shouldDirty: true });
    }
  };

  const hasClockInValidation = isDefined(shiftId);

  const clockInValidation = useClockInTimeValidation({
    selectedTime: hasClockInValidation ? currentTime : undefined,
    shiftId: shiftId ?? "",
    shiftStartAt,
    shiftEndAt,
    workplaceTimezone,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    workplaceSettings: workplaceSettings ?? { preventEarlyClockIns: false },
    isEnforceGeofenceTimestampsEnabled: isEnforceGeofenceTimestampsEnabled ?? false,
    verifiedClockIn,
    workerClockIn,
    clockOutTime,
    breakStartTime,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
    suppressMissedPunchWarning: false,
  });

  const baseRequiresApproval =
    formState.isDirty && isDefined(currentTime) && isBefore(currentTime, originalTime);

  // Also require approval when missed punch warning fires
  const requiresApproval =
    baseRequiresApproval ||
    (hasClockInValidation &&
      isDefined(clockInValidation.intentContent) &&
      clockInValidation.isValid);

  const hasClockInValidationError =
    hasClockInValidation && isDefined(clockInValidation.intent) && !clockInValidation.isValid;

  const showIntent = requiresApproval && !isPickerScrolling;

  // Validation error takes priority, then validation info, then default approval info
  const intentProps = getIntentProps({
    hasClockInValidationError,
    hasClockInValidation,
    showIntent,
    clockInValidation,
  });

  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentTime,
    referenceTimestamp: shiftStartAt,
    workplaceTimezone,
  });

  const displayTime = isDefined(currentTime)
    ? formatTimeWithTimezone(currentTime, workplaceTimezone, false)
    : "-";

  return (
    <TimekeepingAdjustmentBottomSheet
      modalState={modalState}
      {...intentProps}
      pickerTitle="Adjust clock in"
      hasChanges={formState.isDirty}
      isPickerScrolling={isPickerScrolling}
      isPickerValid={
        isDefined(currentTime) &&
        (!hasClockInValidation ||
          (clockInValidation.isValid && !clockInValidation.isValidatingOverlap))
      }
      requiresApproval={requiresApproval}
      reasonTitle="Why did you adjust your clock in time?"
      existingReason={existingReason}
      reasonHelperText="Edit your message to the workplace to explain why you adjusted your clock in time."
      onPickerConfirm={
        hasClockInValidation ? async () => clockInValidation.validateOverlap() : undefined
      }
      onConfirm={(reason) => {
        if (isDefined(currentTime)) {
          onConfirm(currentTime, reason);
        }
      }}
      onCancel={onCancel}
    >
      <Stack direction="row" gap={2} alignItems="center" width="100%">
        <Box
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <CbhIcon size="small" type="clock" />
        </Box>
        <Text bold variant="body2" sx={{ flex: 1 }}>
          Clock in
        </Text>
        <Button
          variant="outlined"
          size="small"
          sx={{
            flexShrink: 0,
            marginLeft: 2,
            backgroundColor: (theme) => theme.palette.common.white,
            pointerEvents: "none",
          }}
        >
          {displayTime}
        </Button>
      </Stack>

      <WheelTimePicker
        preventParentTouchStart
        value={currentLocalTime}
        dayIndicator={dayIndicator}
        containerSx={{ alignSelf: "center" }}
        onChange={handleTimeChange}
        onScrollingChange={setIsPickerScrolling}
      />
    </TimekeepingAdjustmentBottomSheet>
  );
}

interface IntentPropsParams {
  hasClockInValidationError: boolean;
  hasClockInValidation: boolean;
  showIntent: boolean;
  clockInValidation: ReturnType<typeof useClockInTimeValidation>;
}

function getIntentProps(params: IntentPropsParams) {
  const { hasClockInValidationError, hasClockInValidation, showIntent, clockInValidation } = params;

  if (
    hasClockInValidation &&
    (hasClockInValidationError || (showIntent && isDefined(clockInValidation.intentContent)))
  ) {
    return {
      intent: clockInValidation.intent,
      intentIcon: clockInValidation.intentIcon,
      intentContent: clockInValidation.intentContent,
    };
  }

  if (showIntent) {
    return {
      ...getBottomSheetIntent("info"),
      intentContent:
        "You are requesting an earlier clock in time. No worries - you'll be able to add the reason on the next screen.",
    };
  }

  return { intent: undefined, intentIcon: undefined, intentContent: undefined };
}
